// Newsletter Subscribers page - view newsletter subscribers data
// Shows subscriber table with search functionality

/**
 * 
 * NOTES SUMMARY - NEWSLETTER SUBSCRIBERS PAGE
 * 
 * What this page does:
 *  - Shows all newsletter subscribers in a table
 *  - Search box to filter by name or email
 *  - Shows subscriber count at top
 *  - Refresh button to reload data
 *  - Pagination support (25 per page)
 * 
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNewspaper, faSync, faSearch, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import Modal from '../components/common/Modal';
import { formatDate } from '../utils/formatters';
import '../styles/App.css';

function NewsletterSubscribers() {
  // State for subscribers data
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  
  // Mobile menu state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Pagination settings
  const itemsPerPage = 25;
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  const closeSidebar = () => {
    setSidebarOpen(false);
  };
  
  // Load subscribers when component mounts
  useEffect(() => {
    loadSubscribers();
  }, []);
  
  // Function to load newsletter subscribers
  const loadSubscribers = async () => {
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/get-newsletter-subscribers');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      setSubscribers(data.data?.subscribers || []);
      setTotalCount(data.data?.subscriberCount || 0);
      setCurrentPage(1); // Reset to first page on reload
      
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load newsletter subscribers: ' + error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Filter subscribers based on search term
  const filteredSubscribers = subscribers.filter(subscriber => {
    const searchLower = searchTerm.toLowerCase();
    return (
      subscriber.name?.toLowerCase().includes(searchLower) ||
      subscriber.email?.toLowerCase().includes(searchLower)
    );
  });
  
  // Pagination logic
  const totalPages = Math.ceil(filteredSubscribers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubscribers = filteredSubscribers.slice(startIndex, startIndex + itemsPerPage);
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="app-container">
        <Header onMenuClick={toggleSidebar} />
        <div className="app-body">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          <main className="app-main">
            <div className="loading">Loading newsletter subscribers...</div>
          </main>
        </div>
      </div>
    );
  }
  
  return (
    <div className="app-container">
      <Header onMenuClick={toggleSidebar} />
      <div className="app-body">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        <main className="app-main">
          <div className="page-header">
            <h1>
              <FontAwesomeIcon icon={faNewspaper} style={{ marginRight: '0.5rem', color: '#3B82F6' }} />
              Newsletter Subscribers ({totalCount} total)
            </h1>
            <div className="page-header-actions">
              <button onClick={loadSubscribers} className="btn btn-secondary">
                <FontAwesomeIcon icon={faSync} /> Refresh
              </button>
            </div>
          </div>
          
          {/* Search Section */}
          <div className="filters-section">
            <div className="filter-group">
              <label className="filter-label">SEARCH:</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                  }} 
                />
                <input
                  type="text"
                  className="search-input-large"
                  style={{ paddingLeft: '2.5rem' }}
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                />
              </div>
            </div>
            <div className="filter-group">
              <label className="filter-label">RESULTS:</label>
              <span style={{ color: '#374151', fontWeight: '500' }}>
                {paginatedSubscribers.length} of {filteredSubscribers.length} subscribers
              </span>
            </div>
          </div>
          
          {/* Subscribers Table */}
          <div className="lead-table-container">
            {paginatedSubscribers.length > 0 ? (
              <table className="lead-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Date Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubscribers.map((subscriber, index) => (
                    <tr key={index}>
                      <td className="lead-name">
                        {subscriber.name || 'Unknown'}
                      </td>
                      <td style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ color: '#6b7280', fontSize: '0.875rem' }} />
                        {subscriber.email}
                      </td>
                      <td>
                        {subscriber.dateSubscribed ? formatDate(subscriber.dateSubscribed) : 'Unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: '3rem', color: '#d1d5db', marginBottom: '1rem' }} />
                <h3>No Newsletter Subscribers Found</h3>
                <p>
                  {searchTerm 
                    ? `No subscribers match "${searchTerm}"`
                    : 'No newsletter subscribers available yet.'
                  }
                </p>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '1rem', 
              marginTop: '1.5rem',
              padding: '1rem',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <span style={{ color: '#374151', fontWeight: '500' }}>
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
          
          {/* Error Modal */}
          <Modal
            isOpen={modal.isOpen}
            onClose={() => setModal({ ...modal, isOpen: false })}
            title={modal.title}
            message={modal.message}
            type={modal.type}
          />
        </main>
      </div>
    </div>
  );
}

export default NewsletterSubscribers;